<template>
  <div class="material-check-page frame-page settlement-process-page" :class="{ 'is-loading': loading }">
    <div class="page-intro">
      <p>
        正向：上传甲供明细，按「物料编码 + 批次」合并数量后生成结算清单。逆向：将已生成的结算清单还原为甲供明细。
        支持点击选择与拖拽上传。
      </p>
    </div>

    <input
      ref="materialInput"
      type="file"
      hidden
      accept=".xlsx,.xlsm,.xls,.mht,.mhtml"
      @change="onMaterialSelected"
    />
    <input
      ref="settlementInput"
      type="file"
      hidden
      accept=".xlsx,.xlsm,.xls,.mht,.mhtml"
      @change="onSettlementSelected"
    />

    <section class="panel">
      <div class="panel-title">
        <span class="step">1</span>
        <span>正向：甲供明细 → 结算清单</span>
        <span v-if="materialName" class="tag tag-done">已选择</span>
        <span v-else class="tag tag-muted">待选择</span>
      </div>
      <div
        class="drop-zone"
        :class="{
          'is-empty': !materialName,
          'is-dragover': materialDragOver,
          'is-filled': materialName,
        }"
        @click="pickMaterial"
        @dragenter.prevent="materialDragOver = true"
        @dragover.prevent="materialDragOver = true"
        @dragleave.prevent="materialDragOver = false"
        @drop.prevent="onDropMaterial"
      >
        <div class="drop-zone-body">
          <div class="drop-icon" :class="{ 'is-uploaded': materialName }">
            <span v-if="materialName" class="drop-check">✓</span>
            <span v-else>明细</span>
          </div>
          <div class="drop-text">
            <div class="drop-title" :class="{ placeholder: !materialName }">
              {{ materialName || '选择或拖拽甲供明细文件' }}
            </div>
            <div class="drop-hint">
              <template v-if="materialName">已选择 {{ materialName }}</template>
              <template v-else>需含物料编码、物料描述、单位、数量、批次(批次号/采购订单号)、单价 · .xlsx .xlsm .xls .mht .mhtml</template>
            </div>
          </div>
          <div class="drop-actions" @click.stop>
            <Button size="s" class="drop-btn-clear" @click="clearMaterial">清除</Button>
            <Button size="s" color="primary" @click="pickMaterial">浏览</Button>
          </div>
        </div>
      </div>

      <div v-if="sheetNames.length" class="sheet-row">
        <span class="field-label">工作表</span>
        <Select v-model="sheetName" :datas="sheetOptions" filterable style="width: 280px" />
      </div>

      <div class="field-grid">
        <div class="field-item">
          <span class="field-label">项目名称</span>
          <Input v-model="projectName" placeholder="请输入项目名称" />
        </div>
        <div class="field-item">
          <span class="field-label">项目定义号</span>
          <Input v-model="wbsCode" placeholder="请输入项目定义号" />
        </div>
      </div>

      <div class="actions">
        <Button color="primary" size="l" :disabled="!materialFile || !sheetName || !isValidWbsCode(wbsCode) || generating" @click="doGenerate">
          {{ generating ? '生成中…' : '生成结算清单' }}
        </Button>
      </div>
      <div v-if="generateProgress" class="progress-wrap">
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: generateProgress.percent + '%' }"></div>
        </div>
        <div class="progress-text">
          <span>{{ generateProgress.message }}</span>
          <span class="progress-percent">{{ generateProgress.percent }}%</span>
        </div>
      </div>
      <p v-if="generateResult" class="result-hint">
        生成成功：原始 {{ generateResult.rawRowCount }} 行，合并后 {{ generateResult.mergedCount }} 条 ·
        {{ generateResult.filename }}
        <a class="download-link" :href="generateResult.downloadUrl" :download="generateResult.filename">下载</a>
      </p>
    </section>

    <section class="panel">
      <div class="panel-title">
        <span class="step">2</span>
        <span>逆向：结算清单 → 甲供明细</span>
        <span v-if="settlementName" class="tag tag-done">已选择</span>
        <span v-else class="tag tag-muted">待选择</span>
      </div>
      <div
        class="drop-zone"
        :class="{
          'is-empty': !settlementName,
          'is-dragover': settlementDragOver,
          'is-filled': settlementName,
        }"
        @click="pickSettlement"
        @dragenter.prevent="settlementDragOver = true"
        @dragover.prevent="settlementDragOver = true"
        @dragleave.prevent="settlementDragOver = false"
        @drop.prevent="onDropSettlement"
      >
        <div class="drop-zone-body">
          <div class="drop-icon" :class="{ 'is-uploaded': settlementName }">
            <span v-if="settlementName" class="drop-check">✓</span>
            <span v-else>清单</span>
          </div>
          <div class="drop-text">
            <div class="drop-title" :class="{ placeholder: !settlementName }">
              {{ settlementName || '选择或拖拽结算清单文件' }}
            </div>
            <div class="drop-hint">
              <template v-if="settlementName">已选择 {{ settlementName }}</template>
              <template v-else>ZMM020 详细清单或已生成结算清单 · .mht .mhtml .xls .xlsx .xlsm</template>
            </div>
          </div>
          <div class="drop-actions" @click.stop>
            <Button size="s" class="drop-btn-clear" @click="clearSettlement">清除</Button>
            <Button size="s" color="primary" @click="pickSettlement">浏览</Button>
          </div>
        </div>
      </div>

      <div v-if="restoreSheetNames.length" class="sheet-row">
        <span class="field-label">工作表</span>
        <Select v-model="restoreSheetName" :datas="restoreSheetOptions" filterable style="width: 280px" />
      </div>

      <div class="actions">
        <Button color="primary" size="l" :disabled="!settlementFile || !restoreSheetName || restoring" @click="doRestore">
          {{ restoring ? '还原中…' : '还原为甲供明细' }}
        </Button>
      </div>
      <div v-if="restoreProgress" class="progress-wrap">
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: restoreProgress.percent + '%' }"></div>
        </div>
        <div class="progress-text">
          <span>{{ restoreProgress.message }}</span>
          <span class="progress-percent">{{ restoreProgress.percent }}%</span>
        </div>
      </div>
      <p v-if="restoreResult" class="result-hint">
        还原成功：共 {{ restoreResult.rowCount }} 条
        · {{ restoreResult.filename }}
        <a class="download-link" :href="restoreResult.downloadUrl" :download="restoreResult.filename">下载</a>
      </p>
    </section>

    <div class="page-footer-note">
      说明：正向支持 .xlsx/.xlsm/.xls/.mht/.mhtml；批次列可识别「批次 / 批次号 / 采购订单号」等名称。可选工作表，默认优先「求和去重去0明细」，否则第一张。
      逆向同样可选工作表，默认优先 Sheet3，其次「甲供明细」。支持 SAP ZMM020 详细清单（.mht/.mhtml/.xls/.xlsx）及已生成结算清单。
      还原结果含三个工作表——①求和去重明细（默认打开）②甲供明细③求和去重去0明细。
      按物料编码+批次对数量、金额、含税金额求和；保留「数量为 0、有金额」的行并用橙色底纹标注；「去0」表去掉合计数量为 0 的记录。
      有内容的行必须填齐物料编码、物料描述、单位、数量、单价、批次，缺项将提示错误。税率留空时默认 1.13。备注按原值写入结算清单，可留空。
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { confirm, message } from 'heyui';
import { settlementProcessApi as api } from '@/api/settlementProcess';
import '@/views/material-check/material-check.less';

const MATERIAL_EXTS = ['.xlsx', '.xlsm', '.xls', '.mht', '.mhtml'];
const SETTLEMENT_EXTS = ['.xlsx', '.xlsm', '.xls', '.mht', '.mhtml'];

const loading = ref(false);
const generating = ref(false);
const restoring = ref(false);
const generateProgress = ref(null);
const restoreProgress = ref(null);
const materialInput = ref(null);
const settlementInput = ref(null);
const materialDragOver = ref(false);
const settlementDragOver = ref(false);

const materialFile = ref(null);
const materialName = ref('');
const sheetNames = ref([]);
const sheetName = ref('');
const projectName = ref('');
const wbsCode = ref('');
const generateResult = ref(null);

const settlementFile = ref(null);
const settlementName = ref('');
const restoreSheetNames = ref([]);
const restoreSheetName = ref('');
const restoreResult = ref(null);

const sheetOptions = computed(() => sheetNames.value.map((s) => ({ key: s, title: s })));
const restoreSheetOptions = computed(() => restoreSheetNames.value.map((s) => ({ key: s, title: s })));

function matchExtension(name, exts) {
  const lower = (name || '').toLowerCase();
  return exts.some((ext) => lower.endsWith(ext));
}

function isValidWbsCode(code) {
  return (code || '').trim().length >= 12;
}

function filenameAsProjectName(filename) {
  return (filename || '').replace(/\.[^.]+$/, '');
}

async function withLoading(fn) {
  loading.value = true;
  try {
    return await fn();
  } finally {
    loading.value = false;
  }
}

function showError(msg) {
  confirm({
    type: 'error',
    title: '错误',
    content: msg || '操作失败',
  });
}

function resetGenerateStatus() {
  generateProgress.value = null;
  generateResult.value = null;
}

function resetRestoreStatus() {
  restoreProgress.value = null;
  restoreResult.value = null;
}

function pickMaterial() {
  resetGenerateStatus();
  projectName.value = '';
  wbsCode.value = '';
  materialInput.value?.click();
}

function pickSettlement() {
  resetRestoreStatus();
  settlementInput.value?.click();
}

function applyMaterialFile(file) {
  if (!file) return;
  if (!matchExtension(file.name, MATERIAL_EXTS)) {
    showError('甲供明细仅支持 .xlsx / .xlsm / .xls / .mht / .mhtml');
    return;
  }
  withLoading(async () => {
    const resp = await api.probeSheets(file);
    if (!resp.ok) {
      showError(resp.error || '读取工作表失败');
      return;
    }
    materialFile.value = file;
    materialName.value = file.name;
    sheetNames.value = resp.sheetNames || [];
    sheetName.value = resp.sheetName || sheetNames.value[0] || '';
    resetGenerateStatus();
    projectName.value = filenameAsProjectName(file.name);
    wbsCode.value = '';
  });
}

function applySettlementFile(file) {
  if (!file) return;
  if (!matchExtension(file.name, SETTLEMENT_EXTS)) {
    showError('结算清单仅支持 .mht .mhtml .xls .xlsx .xlsm');
    return;
  }
  withLoading(async () => {
    const resp = await api.probeSheets(file, 'restore');
    if (!resp.ok) {
      showError(resp.error || '读取工作表失败');
      return;
    }
    settlementFile.value = file;
    settlementName.value = file.name;
    restoreSheetNames.value = resp.sheetNames || [];
    restoreSheetName.value = resp.sheetName || restoreSheetNames.value[0] || '';
    resetRestoreStatus();
  });
}

function onMaterialSelected(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  applyMaterialFile(file);
}

function onSettlementSelected(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  applySettlementFile(file);
}

function onDropMaterial(event) {
  materialDragOver.value = false;
  applyMaterialFile(event.dataTransfer?.files?.[0]);
}

function onDropSettlement(event) {
  settlementDragOver.value = false;
  applySettlementFile(event.dataTransfer?.files?.[0]);
}

function clearMaterial() {
  materialFile.value = null;
  materialName.value = '';
  projectName.value = '';
  wbsCode.value = '';
  sheetNames.value = [];
  sheetName.value = '';
  resetGenerateStatus();
}

function clearSettlement() {
  settlementFile.value = null;
  settlementName.value = '';
  restoreSheetNames.value = [];
  restoreSheetName.value = '';
  resetRestoreStatus();
}

async function doGenerate() {
  if (!materialFile.value) {
    showError('请先选择甲供明细文件');
    return;
  }
  if (!sheetName.value) {
    showError('请选择工作表');
    return;
  }
  if (!projectName.value.trim()) {
    showError('请输入项目名称');
    return;
  }
  if (!isValidWbsCode(wbsCode.value)) {
    showError('项目定义号至少 12 位，如 181314230001');
    return;
  }
  generating.value = true;
  generateResult.value = null;
  generateProgress.value = { percent: 0, message: '准备中...' };
  try {
    const resp = await api.generate(
      materialFile.value,
      projectName.value.trim(),
      wbsCode.value.trim(),
      sheetName.value,
      (percent, message) => {
        generateProgress.value = { percent, message };
      }
    );
    if (!resp.ok) {
      generateProgress.value = null;
      showError(resp.error);
      return;
    }
    generateProgress.value = { percent: 100, message: '生成完成' };
    generateResult.value = {
      filename: resp.filename,
      path: resp.path || '',
      rawRowCount: resp.rawRowCount || 0,
      mergedCount: resp.mergedCount || 0,
      downloadUrl: api.downloadUrl(resp.filename),
    };
    message.success(
      resp.path
        ? `结算清单已生成：${resp.path}`
        : `结算清单已生成：原始 ${resp.rawRowCount} 行，合并后 ${resp.mergedCount} 条`
    );
  } finally {
    generating.value = false;
  }
}

async function doRestore() {
  if (!settlementFile.value) {
    showError('请先选择结算清单文件');
    return;
  }
  if (!restoreSheetName.value) {
    showError('请选择工作表');
    return;
  }
  restoring.value = true;
  restoreResult.value = null;
  restoreProgress.value = { percent: 0, message: '准备中...' };
  try {
    const resp = await api.restore(settlementFile.value, restoreSheetName.value, (percent, message) => {
      restoreProgress.value = { percent, message };
    });
    if (!resp.ok) {
      restoreProgress.value = null;
      showError(resp.error);
      return;
    }
    restoreProgress.value = { percent: 100, message: '还原完成' };
    restoreResult.value = {
      filename: resp.filename,
      path: resp.path || '',
      rowCount: resp.rowCount || 0,
      projectName: resp.projectName || '',
      downloadUrl: api.downloadUrl(resp.filename),
    };
    message.success(
      resp.path ? `甲供明细已还原：${resp.path}` : `甲供明细已还原：共 ${resp.rowCount} 条`
    );
  } finally {
    restoring.value = false;
  }
}
</script>

<style lang="less" scoped>
.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 24px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e8ecf1;
}

.sheet-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.field-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  color: #8b95a5;
  font-size: 13px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
}

.progress-wrap {
  margin-top: 16px;
}

.progress-track {
  height: 8px;
  border-radius: 999px;
  background: #eef1f5;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 999px;
  background: #3788ee;
  transition: width 0.2s ease;
}

.progress-text {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  font-size: 13px;
  color: #8b95a5;
  line-height: 1.5;
}

.progress-percent {
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
  color: #3788ee;
  font-weight: 600;
}

.result-hint {
  margin: 16px 0 0;
  font-size: 13px;
  color: #8b95a5;
  line-height: 1.6;
}

.download-link {
  margin-left: 8px;
  color: #3788ee;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

@media (max-width: 720px) {
  .field-grid {
    grid-template-columns: 1fr;
  }
}
</style>
